/**
 * Spatial-dataset display_style adapter. GeoStyler is the runtime/export format.
 *
 * Two responsibilities live here:
 *  - READ: upgrade a legacy flat paint dict (`circle-color`, `raster-opacity`, …)
 *    to a one-rule GeoStyler `Style` (`spatialDatasetStyleToGeostyler`), or pass a
 *    stored GeoStyler style through untouched.
 *  - WRITE: the vector category editor and the raster Tier-1 controls both write
 *    through here. Vector writing deliberately reuses `cmTableStyle.ts`'s
 *    engine (`getStyleChoices`/`buildUpdatedStyle`) rather than a second copy —
 *    that module already fixed three GeoStyler round-tripping bugs (dropping
 *    `__label`-style reserved rules, flattening per-rule symbolizer properties,
 *    filtering on the wrong property) that a from-scratch writer would only
 *    relearn. See the 9 Sep 2026 spatial-dataset styling plan, §3.1/§5.
 */
import type { RasterSymbolizer, Style } from "geostyler-style";
import { toCategoryValues } from "./cmTableFieldValues";
import {
  baseGeometry,
  buildUpdatedStyle,
  getStyleChoices,
  getStyleDefaultOutput,
  isIconStyled,
  type CmIconSize,
  type CmSpatialType,
  type CmStyleChoice
} from "./cmTableStyle";

export type SpatialGeometryType = "point" | "line" | "polygon" | "raster";

/** A vector spatial dataset's worker-reported geometry family, or none yet. */
export type SpatialVectorGeometryType = "point" | "line" | "polygon" | null | undefined;

export function isGeostylerStyle(value: unknown): value is Style {
  return !!value && typeof value === "object" && Array.isArray((value as Style).rules);
}

/**
 * Maps a spatial dataset's `geometry_type` onto the geometry-family vocabulary
 * `cmTableStyle.ts` already models (which additionally distinguishes multi- from
 * single-geometry tables — a spatial dataset never needs that distinction, since
 * the worker writes one geometry family per dataset). `"line"` → `"linestring"`
 * is the one real translation; everything else passes through or falls to
 * `"none"` (no geometry classified yet, or a raster).
 */
export function toCmSpatialType(geometryType: SpatialVectorGeometryType): CmSpatialType {
  if (geometryType === "point") return "point";
  if (geometryType === "polygon") return "polygon";
  if (geometryType === "line") return "linestring";
  return "none";
}

/**
 * Temporary read adapter for pre-migration dataset styles. New/updated styles must
 * be stored as GeoStyler; backend migration removes the need for this branch.
 */
export function spatialDatasetStyleToGeostyler(
  displayStyle: Record<string, any> | undefined,
  geometry: SpatialGeometryType,
  name = "spatial-dataset"
): Style {
  if (isGeostylerStyle(displayStyle)) return displayStyle;
  const legacy = displayStyle ?? {};
  if (geometry === "raster") {
    return {
      name,
      rules: [
        {
          name: "default",
          symbolizers: [{ kind: "Raster", opacity: legacy["raster-opacity"] ?? 1 }]
        }
      ]
    } as Style;
  }
  if (geometry === "point") {
    return {
      name,
      rules: [
        {
          name: "default",
          symbolizers: [
            {
              kind: "Mark",
              wellKnownName: "circle",
              radius: legacy["circle-radius"] ?? 5,
              color: legacy["circle-color"] ?? "#3b82f6",
              fillOpacity: legacy["circle-opacity"],
              strokeColor: legacy["circle-stroke-color"],
              strokeWidth: legacy["circle-stroke-width"]
            }
          ]
        }
      ]
    } as Style;
  }
  if (geometry === "line") {
    return {
      name,
      rules: [
        {
          name: "default",
          symbolizers: [
            {
              kind: "Line",
              color: legacy["line-color"] ?? "#3b82f6",
              width: legacy["line-width"] ?? 2,
              opacity: legacy["line-opacity"],
              dasharray: legacy["line-dasharray"]
            }
          ]
        }
      ]
    } as Style;
  }
  return {
    name,
    rules: [
      {
        name: "default",
        symbolizers: [
          {
            kind: "Fill",
            color: legacy["fill-color"] ?? "#3b82f6",
            opacity: legacy["fill-opacity"] ?? 0.5,
            outlineColor: legacy["fill-outline-color"]
          }
        ]
      }
    ]
  } as Style;
}

/**
 * The geometry family a stored GeoStyler style implies, from the symbolizers it
 * actually uses: Icon/Mark → point, Line → line, Fill → polygon.
 *
 * Only for datasets whose `geometry_type` the worker never reported. Reading the
 * style is the one remaining signal, and it has to be read as GeoStyler — the
 * legacy guess (look for `line-*` / `circle-*` keys) inspects a flat paint dict,
 * and a GeoStyler document has none of those keys at its top level, so it always
 * guessed "polygon". A point dataset then rendered as a fill layer, which draws
 * nothing at all against point tiles: styling it made it disappear.
 *
 * Returns undefined when the style isn't GeoStyler or carries no symbolizer we
 * can place, so the caller's own fallback still applies.
 */
export function geometryTypeFromStyle(displayStyle: Record<string, any> | undefined): SpatialVectorGeometryType {
  if (!isGeostylerStyle(displayStyle)) return undefined;
  for (const rule of displayStyle.rules ?? []) {
    for (const symbolizer of (rule as any)?.symbolizers ?? []) {
      switch (symbolizer?.kind) {
        case "Icon":
        case "Mark":
          return "point";
        case "Line":
          return "line";
        case "Fill":
          return "polygon";
        default:
          break;
      }
    }
  }
  return undefined;
}

// ─── Vector write side — category editor ───────────────────────────────────────
//
// A spatial dataset's rows come straight from the original upload's columns
// (via tippecanoe), unlike a CM table where the backend serializer flattens a
// row's category under a fixed `style_value` property. So there is no
// style-key-vs-filter-property split to worry about (cmTableStyle.ts's T1
// trap): the field the editor lets someone pick IS the property the rules
// filter on. These wrappers exist only to translate the geometry vocabulary
// (`toCmSpatialType`) — `cmTableStyle.ts` owns every actual styling rule.

/** True when this dataset's geometry family styles by icon rather than colour. */
export const isSpatialVectorIconStyled = (geometryType: SpatialVectorGeometryType): boolean => isIconStyled(toCmSpatialType(geometryType));

/**
 * Distinct values per field in `sample_values` — a preview, from file order.
 * Unchanged by the full-scan worker change.
 */
export const SAMPLE_VALUES_CAP = 10;

/**
 * The worker's ceiling for the complete `values` list, inclusive: a column with
 * exactly this many distinct values is kept in full, 501 abandons enumeration.
 * Doubles as the editor's prefill cap — see {@link SpatialFieldValues.values}.
 */
export const ENUMERATE_MAX = 500;

export interface SpatialFieldValues {
  /** The values available to prefill categories with, normalised and sorted. */
  values: string[];
  /**
   * True when this is the COMPLETE distinct set. Determined solely by whether the
   * worker sent `values` — absent means unknown, never "complete".
   */
  complete: boolean;
  /** Exact distinct total. Only ever present alongside a complete list. */
  distinctCount?: number;
  /**
   * The column reads as a key, not a category. Independent of {@link complete}: a
   * fully enumerated column can still be flagged.
   */
  highCardinality: boolean;
  /**
   * True when the field is absent from `attribute_schema` entirely — a column name
   * typed by hand into the freeSolo picker. Nothing is known about its values, so
   * the UI must claim nothing about them either (not even "incomplete").
   */
  unknownField: boolean;
  /** Which key the values came from, so the limitation is legible. */
  source: "values" | "sample_values" | "none";
}

/**
 * The category values available for one vector attribute.
 *
 * Two INDEPENDENT signals arrive from the worker and are deliberately not collapsed:
 *
 *  - **`values`** — present ⟺ the distinct set is complete (capped at
 *    {@link ENUMERATE_MAX}). Absent ⟹ *unknown*: the column passed the ceiling, or
 *    the dataset predates the full-feature scan. An earlier version of this function
 *    inferred completeness from `sample_values.length >= 10`, which reported a
 *    three-value column as complete when nothing was actually known about it.
 *  - **`high_cardinality`** — the column reads as a key (`osm_id`) rather than a
 *    category. Both can be true at once: 300 genuine categories over 400 features is
 *    a complete list that still deserves a warning, and must not auto-prefill.
 *
 * `high_cardinality: true` with `values` absent means **more than
 * {@link ENUMERATE_MAX} distinct values** — the only other route to that state is a
 * column of unhashable values, which is not styleable either way, so the inference
 * is safe to show.
 */
export function spatialFieldValues(attributeSchema: Array<Record<string, any>> | null | undefined, fieldName: string): SpatialFieldValues {
  const field = (attributeSchema ?? []).find((entry) => entry?.name === fieldName);
  if (!field) {
    return { values: [], complete: false, highCardinality: false, unknownField: true, source: "none" };
  }

  const full = Array.isArray(field.values) ? field.values : undefined;
  const sample = Array.isArray(field.sample_values) ? field.sample_values : undefined;
  const raw = full ?? sample ?? [];

  // Re-sorted here even though `values` arrives sorted: the worker sorts numeric
  // STRINGS lexically (they are strings to it), so "10" would precede "2".
  const values = [...new Set(raw.flatMap(toCategoryValues))].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
  );

  return {
    values,
    complete: !!full,
    distinctCount: typeof field.distinct_count === "number" ? field.distinct_count : undefined,
    highCardinality: field.high_cardinality === true,
    unknownField: false,
    source: full ? "values" : sample ? "sample_values" : "none"
  };
}

/** Selectable category choices (value + icon/colour) read from a stored style. */
export function getSpatialVectorStyleChoices(
  style: Record<string, any> | undefined,
  geometryType: SpatialVectorGeometryType
): CmStyleChoice[] {
  return getStyleChoices(style, toCmSpatialType(geometryType));
}

/** The fallback (default-rule) icon/colour for this geometry family. */
export function getSpatialVectorStyleDefaultOutput(
  style: Record<string, any> | undefined,
  geometryType: SpatialVectorGeometryType
): string | undefined {
  return getStyleDefaultOutput(style, toCmSpatialType(geometryType));
}

/**
 * The attribute column a stored vector style's category rules currently filter
 * on, read from the first simple `["==", property, value]` rule. Undefined when
 * the style has no category rules yet (a fresh dataset, or colour/icon-only
 * default) — the editor should then let the user pick a column rather than
 * defaulting to a placeholder no real column is likely to be named.
 */
export function getSpatialVectorStyleFieldKey(style: Record<string, any> | undefined): string | undefined {
  if (!isGeostylerStyle(style)) return undefined;
  for (const rule of style.rules ?? []) {
    const filter = (rule as any)?.filter;
    if (Array.isArray(filter) && filter[0] === "==" && typeof filter[1] === "string" && filter[1]) return filter[1];
  }
  return undefined;
}

/**
 * Rebuilds a vector spatial dataset's style from the category editor's choices.
 * `fieldKey` is the real attribute column the rules should filter on (see
 * {@link getSpatialVectorStyleFieldKey} — there is no separate storage-key
 * indirection to get wrong here, unlike CM tables).
 */
export function buildUpdatedSpatialVectorStyle(
  style: Record<string, any> | undefined,
  geometryType: SpatialVectorGeometryType,
  fieldKey: string,
  choices: CmStyleChoice[],
  defaultChoice?: CmStyleChoice,
  iconSize?: CmIconSize
): Record<string, any> {
  return buildUpdatedStyle(style, toCmSpatialType(geometryType), fieldKey, choices, defaultChoice, iconSize);
}

/** Re-exported so callers don't need a second import from `cmTableStyle.ts` just for this. */
export const spatialBaseGeometry = baseGeometry;

// ─── Raster write side — Tier 1 only ────────────────────────────────────────────
//
// `RasterSymbolizer` splits into two feasibility tiers (plan §4). Tier 1 —
// opacity/brightness/contrast/saturation/hueRotate/resampling/fadeDuration — is
// a direct 1:1 map to MapLibre raster-layer paint properties and works for any
// raster (COG or pre-baked PMTiles). Tier 2 — `colorMap` (value→colour) and
// `channelSelection` (band→RGB) — needs per-pixel access only a COG's client-side
// decode can provide, and is currently blocked on the worker writing raw-value
// COGs for single-band input (D-6). Only Tier 1 is modelled here; `colorMap` /
// `channelSelection` on an existing style are preserved untouched by
// `buildUpdatedRasterStyle` so a future Tier-2 editor has something to build on.

export interface SpatialRasterTier1Values {
  opacity?: number;
  hueRotate?: number;
  brightnessMin?: number;
  brightnessMax?: number;
  saturation?: number;
  contrast?: number;
  resampling?: "linear" | "nearest";
  fadeDuration?: number;
}

const RASTER_TIER1_KEYS: (keyof SpatialRasterTier1Values)[] = [
  "opacity",
  "hueRotate",
  "brightnessMin",
  "brightnessMax",
  "saturation",
  "contrast",
  "resampling",
  "fadeDuration"
];

/** The first Raster symbolizer in a GeoStyler style, across all rules. */
export function findRasterSymbolizer(style: Style | undefined): RasterSymbolizer | undefined {
  for (const rule of style?.rules ?? []) {
    const sym = (rule.symbolizers ?? []).find((s): s is RasterSymbolizer => s.kind === "Raster");
    if (sym) return sym;
  }
  return undefined;
}

/** Reads the Tier-1 raster controls from a dataset's stored style (any format). */
export function getRasterTier1Values(displayStyle: Record<string, any> | undefined): SpatialRasterTier1Values {
  const style = spatialDatasetStyleToGeostyler(displayStyle, "raster");
  const sym = findRasterSymbolizer(style);
  const values: SpatialRasterTier1Values = {};
  RASTER_TIER1_KEYS.forEach((key) => {
    const v = (sym as any)?.[key];
    if (v !== undefined) (values as any)[key] = v;
  });
  return values;
}

/**
 * Writes Tier-1 values onto the dataset's Raster symbolizer. Preserves anything
 * else already on that symbolizer (a hand-authored `colorMap` in the Advanced
 * JSON tab, once Tier 2 unblocks) and any other rules verbatim, mirroring
 * `buildUpdatedStyle`'s "never drop what the form doesn't own" rule.
 */
export function buildUpdatedRasterStyle(
  displayStyle: Record<string, any> | undefined,
  values: SpatialRasterTier1Values,
  name = "spatial-dataset"
): Record<string, any> {
  const style = spatialDatasetStyleToGeostyler(displayStyle, "raster", name);
  const existingRules = style.rules ?? [];
  const existingSym = findRasterSymbolizer(style);
  const otherRules = existingRules.filter((rule) => !(rule.symbolizers ?? []).some((s: any) => s?.kind === "Raster"));

  const symbolizer: Record<string, any> = { ...(existingSym ?? {}), kind: "Raster" };
  RASTER_TIER1_KEYS.forEach((key) => {
    const v = values[key];
    if (v !== undefined) (symbolizer as any)[key] = v;
  });

  return {
    ...(isGeostylerStyle(displayStyle) ? (displayStyle as Record<string, any>) : {}),
    format: "geostyler",
    version: 1,
    name: typeof style.name === "string" ? style.name : name,
    rules: [{ name: "default", symbolizers: [symbolizer] }, ...otherRules]
  };
}
